import {useState, type FormEvent} from 'react'
import {useTranslation} from 'react-i18next'
import {PorQue} from '@/components/ui/por-que'
import {Link} from 'react-router-dom'
import {Button} from '@/components/ui/button'
import {Checkbox} from '@/components/ui/checkbox'
import {Dialog, DialogContent, DialogHeader, DialogTitle} from '@/components/ui/dialog'
import {Input} from '@/components/ui/input'
import {Skeleton} from '@/components/ui/skeleton'
import {Tag} from '@/components/ui/tag'
import {HelpIcon} from '@/components/help-icon'
import {PaginationBar} from '@/components/ui/pagination-bar'
import {CifraModal} from '@/components/cifra-modal'
import {DescargarEvidencia} from '@/components/descargar-evidencia'
import {EliminarConMotivoDialog} from '@/components/eliminar-con-motivo-dialog'
import {FieldError} from '@/components/field-error'
import {FormDialogFooter} from '@/components/form-dialog-footer'
import {UploadTrigger} from '@/components/upload-trigger'
import {useCapabilities} from '@/hooks/empresa/organizacion/use-businesses'
import {useCifras} from '@/hooks/empresa/organizacion/use-cifras'
import {
  addMonthsIso,
  mintUploadTarget,
  rutaDeDescarga,
  uploadToStorage,
  useEmployeeEvidence,
  useEvidenceUploadFlow,
} from '@/hooks/sgsst/evidencias/use-evidence'
import {useGodModeReadOnly} from '@/hooks/platform/use-god-mode'
import {
  useAnularValoracion,
  useRegistrarValoracion,
  useValoraciones,
  type Exigencia,
  type NormaValoracion,
  type Valoracion,
  type ValoracionClase,
} from '@/hooks/salud/medicina_laboral/use-valoraciones'
import {esFechaFuturaSinConfirmar, translateApiError} from '@/lib/errors'
import {formatDateOnly} from '@/lib/risk'
import {soporteEstadoTone} from '@/lib/sgsst-readiness'
import {validateRequired} from '@/lib/validation'
import {tope} from '@/lib/campos-texto'

/** Vigencia que fija la empresa mientras Inci no verifique la periodicidad de cada norma (D-O, caso c). */
const CIFRA_VIGENCIA = 'vigencia_valoracion_meses'

type SoporteSubido = {object_key: string; filename: string; content_type: string; size_bytes: number}

/** El vencimiento pre-llenado solo se puede acortar, con motivo; el veredicto sale del catálogo de su norma. */
function RegistrarValoracionDialog({
  businessId,
  employeeId,
  catalogo,
  claseInicial,
  open,
  onOpenChange,
}: {
  businessId: string
  employeeId: string
  catalogo: NormaValoracion[]
  claseInicial: ValoracionClase | null
  open: boolean
  onOpenChange: (next: boolean) => void
}) {
  const {t} = useTranslation()
  const registrables = catalogo.filter((norma) => norma.veredictos.length > 0)
  const primera = claseInicial ?? registrables[0]?.clase ?? 'alturas'
  const [clase, setClase] = useState<ValoracionClase>(primera)
  const [veredicto, setVeredicto] = useState(registrables.find((n) => n.clase === primera)?.veredictos[0] ?? '')
  const [fecha, setFecha] = useState('')
  const [venceEl, setVenceEl] = useState('')
  const [motivo, setMotivo] = useState('')
  // «Posterior a hoy» lo decide el backend (`bogota_today`), nunca el reloj del navegador:
  // la casilla de confirmación aparece con su 422.
  const [fechaFutura, setFechaFutura] = useState(false)
  const [fechaConfirmada, setFechaConfirmada] = useState(false)
  const [atestacion, setAtestacion] = useState(false)
  const [soporte, setSoporte] = useState<SoporteSubido | null>(null)
  const subida = useEvidenceUploadFlow(async (file, onProgress) => {
    const target = await mintUploadTarget(businessId, file)
    await uploadToStorage(target, file, onProgress)
    setSoporte({
      object_key: target.object_key,
      filename: file.name,
      content_type: file.type,
      size_bytes: file.size,
    })
  })
  const [fechaError, setFechaError] = useState<string | undefined>()
  const [venceError, setVenceError] = useState<string | undefined>()
  const [error, setError] = useState<string | null>(null)
  const registrar = useRegistrarValoracion(businessId, employeeId)
  const norma = registrables.find((fila) => fila.clase === clase)
  const prellenado = norma?.meses != null && fecha ? addMonthsIso(new Date(`${fecha}T00:00:00`), norma.meses) : ''
  const acorto = !!prellenado && !!venceEl && venceEl < prellenado

  function cambiarFecha(next: string) {
    setFecha(next)
    setFechaError(undefined)
    setVenceError(undefined)
    // Una confirmación vieja no puede viajar con una fecha nueva.
    setFechaFutura(false)
    setFechaConfirmada(false)
    const norma_ = registrables.find((fila) => fila.clase === clase)
    setVenceEl(norma_?.meses != null && next ? addMonthsIso(new Date(`${next}T00:00:00`), norma_.meses) : '')
  }

  function cambiarClase(siguiente: ValoracionClase) {
    setClase(siguiente)
    const norma_ = registrables.find((fila) => fila.clase === siguiente)
    setVeredicto(norma_?.veredictos[0] ?? '')
    setVenceEl(norma_?.meses != null && fecha ? addMonthsIso(new Date(`${fecha}T00:00:00`), norma_.meses) : '')
    setMotivo('')
    setVenceError(undefined)
  }

  async function submit(e: FormEvent) {
    e.preventDefault()
    const problema = validateRequired(fecha)
    setFechaError(problema ?? undefined)
    if (problema) return
    if (prellenado && venceEl && venceEl > prellenado) {
      setVenceError(t('valoracion.registrar.venceElMaximo', {fecha: formatDateOnly(prellenado)}))
      return
    }
    const motivoProblema = acorto ? validateRequired(motivo) : null
    if (motivoProblema) {
      setError(t('valoracion.errors.acortamientoSinMotivo'))
      return
    }
    setError(null)
    try {
      await registrar.mutateAsync({
        clase,
        veredicto,
        fecha,
        vence_el: venceEl || null,
        acortamiento_motivo: acorto ? motivo.trim() : null,
        fecha_confirmada: fechaConfirmada,
        atestacion,
        ...(soporte ?? {}),
      })
      onOpenChange(false)
    } catch (err) {
      // No cierra ni limpia: el 422 de fecha futura se contesta con el formulario aún escrito.
      if (esFechaFuturaSinConfirmar(err)) setFechaFutura(true)
      setError(translateApiError(err))
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      {/* Sin este alto máximo el botón de registrar queda fuera de la pantalla. */}
      <DialogContent className="max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{t('valoracion.registrar.title')}</DialogTitle>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-4" noValidate>
          <p className="text-sm text-muted-foreground">{t('valoracion.registrar.description')}</p>
          {/* Radios y no desplegable: el veredicto son las palabras de la norma y se lee entero antes de elegir. */}
          <fieldset className="space-y-2">
            <legend className="font-mono text-[10px] font-medium tracking-wider text-muted-foreground uppercase">
              {t('valoracion.registrar.claseLabel')}
            </legend>
            {registrables.map((fila) => (
              <label key={fila.clase} className="flex cursor-pointer items-start gap-2 text-sm">
                <input
                  type="radio"
                  name="valoracion-clase"
                  value={fila.clase}
                  data-testid={`valoracion-registrar-clase-${fila.clase}`}
                  checked={clase === fila.clase}
                  onChange={() => cambiarClase(fila.clase)}
                  className="mt-0.5 accent-primary"
                />
                <span>
                  {t(`valoracion.clase.${fila.clase}`)}
                  <span className="ml-1.5 text-xs text-muted-foreground">
                    {t('valoracion.segunNorma', {norma: fila.norma_nombre})}
                  </span>
                </span>
              </label>
            ))}
          </fieldset>
          <fieldset className="space-y-2">
            <legend className="font-mono text-[10px] font-medium tracking-wider text-muted-foreground uppercase">
              {t('valoracion.registrar.veredictoLabel')}
            </legend>
            {(norma?.veredictos ?? []).map((valor) => (
              <label key={valor} className="flex cursor-pointer items-start gap-2 text-sm">
                <input
                  type="radio"
                  name="valoracion-veredicto"
                  value={valor}
                  data-testid={`valoracion-registrar-veredicto-${valor.replace(/\s+/g, '-')}`}
                  checked={veredicto === valor}
                  onChange={() => setVeredicto(valor)}
                  className="mt-0.5 accent-primary"
                />
                <span>{valor}</span>
              </label>
            ))}
          </fieldset>
          <label className="block space-y-1">
            <span className="font-mono text-[10px] font-medium tracking-wider text-muted-foreground uppercase">
              {t('valoracion.registrar.fechaLabel')}
            </span>
            <Input
              type="date"
              value={fecha}
              data-testid="valoracion-registrar-fecha"
              onChange={(e) => cambiarFecha(e.target.value)}
              onBlur={(e) => setFechaError(validateRequired(e.target.value) ?? undefined)}
            />
            <FieldError error={fechaError} />
          </label>
          <label className="block space-y-1">
            <span className="font-mono text-[10px] font-medium tracking-wider text-muted-foreground uppercase">
              {t('valoracion.registrar.venceElLabel')}
            </span>
            <Input
              type="date"
              value={venceEl}
              min={fecha || undefined}
              max={prellenado || undefined}
              data-testid="valoracion-registrar-vence-el"
              onChange={(e) => {
                setVenceEl(e.target.value)
                setVenceError(undefined)
              }}
              onBlur={(e) =>
                setVenceError(
                  prellenado && e.target.value && e.target.value > prellenado
                    ? t('valoracion.registrar.venceElMaximo', {fecha: formatDateOnly(prellenado)})
                    : undefined
                )
              }
            />
            {prellenado && (
              <p className="text-xs text-muted-foreground" data-testid="valoracion-registrar-prellenado">
                {t('valoracion.registrar.venceElPrellenado', {fecha: formatDateOnly(prellenado)})}
              </p>
            )}
            {!prellenado && norma?.meses != null && (
              <p className="text-xs text-muted-foreground">{t('valoracion.conPeriodicidad', {meses: norma.meses})}</p>
            )}
            <FieldError error={venceError} />
          </label>
          {acorto && (
            <label className="block space-y-1">
              <span className="font-mono text-[10px] font-medium tracking-wider text-muted-foreground uppercase">
                {t('valoracion.registrar.motivoLabel')}
              </span>
              <Input
                maxLength={tope('NOTA')}
                value={motivo}
                data-testid="valoracion-registrar-motivo"
                onChange={(e) => setMotivo(e.target.value)}
              />
            </label>
          )}
          <div className="space-y-1">
            <span className="font-mono text-[10px] font-medium tracking-wider text-muted-foreground uppercase">
              {t('valoracion.registrar.certificadoLabel')}
            </span>
            <div className="flex flex-wrap items-center gap-2">
              <UploadTrigger
                label={t('valoracion.registrar.certificadoAdjuntar')}
                progress={subida.progress}
                testId="valoracion-registrar-certificado"
                onPick={subida.start}
              />
              {soporte && (
                <span className="text-xs text-muted-foreground" data-testid="valoracion-registrar-certificado-nombre">
                  {t('valoracion.registrar.certificadoAdjunto', {filename: soporte.filename})}
                </span>
              )}
            </div>
          </div>
          {/* §4: la atestación se pide por documento. */}
          {soporte && (
            <label className="flex items-start gap-2 text-sm">
              <Checkbox
                checked={atestacion}
                data-testid="valoracion-registrar-atestacion"
                onCheckedChange={(next) => setAtestacion(next === true)}
              />
              <span className="text-muted-foreground">{t('valoracion.registrar.atestacionLabel')}</span>
            </label>
          )}
          {/* Solo tras el 422 del servidor: ofrecida siempre, se marcaba por costumbre. */}
          {fechaFutura && (
            <label className="flex items-start gap-2 text-sm">
              <Checkbox
                checked={fechaConfirmada}
                data-testid="valoracion-registrar-fecha-confirmada"
                onCheckedChange={(next) => setFechaConfirmada(next === true)}
              />
              <span className="text-muted-foreground">{t('valoracion.registrar.fechaConfirmadaLabel')}</span>
            </label>
          )}
          <FormDialogFooter
            error={error ?? subida.error}
            pending={registrar.isPending}
            submitLabel={t('valoracion.registrar.submit')}
            onCancel={() => onOpenChange(false)}
          />
        </form>
      </DialogContent>
    </Dialog>
  )
}

/** Anular no es borrar (§4): la fila sigue visible, marcada y con su motivo. */
function AnularValoracionDialog({
  businessId,
  employeeId,
  valoracion,
}: {
  businessId: string
  employeeId: string
  valoracion: Valoracion
}) {
  const {t} = useTranslation()
  const [open, setOpen] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const anular = useAnularValoracion(businessId, employeeId)

  async function confirmar(motivo: string) {
    setError(null)
    try {
      await anular.mutateAsync({valoracionId: valoracion.id, motivo})
      setOpen(false)
    } catch (err) {
      setError(translateApiError(err))
    }
  }

  return (
    <>
      <Button
        variant="ghost"
        size="sm"
        data-testid={`valoracion-anular-${valoracion.id}`}
        onClick={() => setOpen(true)}>
        {t('valoracion.anular.action')}
      </Button>
      <EliminarConMotivoDialog
        open={open}
        onOpenChange={setOpen}
        title={t('valoracion.anular.title')}
        description={t('valoracion.anular.description')}
        motivoLabel={t('valoracion.anular.motivoLabel')}
        motivoPlaceholder={t('valoracion.anular.motivoPlaceholder')}
        confirmLabel={t('valoracion.anular.action')}
        testIdPrefix="valoracion-anular"
        pending={anular.isPending}
        error={error}
        onConfirm={confirmar}
        onReset={() => setError(null)}
      />
    </>
  )
}

/** Los tres casos de D-O: sin norma no abre; sin periodicidad verificada abre y ofrece fijarla, nunca en rojo. */
function FilaExigencia({
  exigencia,
  norma,
  puedeFijar,
  puedeRegistrar,
  onFijarCifra,
  onRegistrar,
}: {
  exigencia: Exigencia
  norma: NormaValoracion | undefined
  puedeFijar: boolean
  puedeRegistrar: boolean
  onFijarCifra: () => void
  onRegistrar: () => void
}) {
  const {t} = useTranslation()
  return (
    <li
      className="flex flex-wrap items-start justify-between gap-3 py-3"
      data-testid={`valoracion-exigencia-${exigencia.clase}`}>
      <div className="min-w-0">
        <p className="text-sm font-medium text-foreground">
          {t(`valoracion.clase.${exigencia.clase}`)}
          {norma && (
            <span className="ml-1.5 text-xs font-normal text-muted-foreground">
              {t('valoracion.segunNorma', {norma: norma.norma_nombre})}
            </span>
          )}
        </p>
        {exigencia.desde && (
          <p className="mt-0.5 font-mono text-[11px] text-muted-foreground">
            {t('valoracion.desde', {fecha: formatDateOnly(exigencia.desde)})}
          </p>
        )}
        {norma?.estado_norma === 'sin_norma_cargada' && (
          <p className="mt-0.5 text-xs text-muted-foreground">{t('valoracion.sinNormaCargada')}</p>
        )}
        {norma?.estado_norma === 'sin_periodicidad_verificada' && (
          <p className="mt-0.5 text-xs text-muted-foreground">
            {t('valoracion.sinPeriodicidad')}{' '}
            {/* Fijar la cifra es una decisión con autor: mismo gate que registrar (`sgsst_firmar`, sin modo dios). */}
            {puedeFijar && (
              <button
                type="button"
                className="text-primary hover:underline"
                data-testid={`valoracion-fijar-cifra-${exigencia.clase}`}
                onClick={onFijarCifra}>
                {t('valoracion.fijelo')}
              </button>
            )}
          </p>
        )}
        {norma?.estado_norma === 'con_periodicidad' && norma.meses != null && (
          <p className="mt-0.5 text-xs text-muted-foreground">
            {t('valoracion.conPeriodicidad', {meses: norma.meses})}
          </p>
        )}
        <Tag tone={soporteEstadoTone(exigencia.estado)} className="mt-1">
          {t(exigencia.cubierta ? 'valoracion.cubierta' : 'valoracion.faltaCertificado')}
        </Tag>
      </div>
      {puedeRegistrar && !exigencia.cubierta && norma?.estado_norma !== 'sin_norma_cargada' && (
        <Button
          size="sm"
          variant="outline"
          data-testid={`valoracion-registrar-${exigencia.clase}`}
          onClick={onRegistrar}>
          {t('valoracion.registrar.action')}
        </Button>
      )}
    </li>
  )
}

/** «Aportada, no exigida», «ya no exigible» y la anulación marcan la fila; nunca la borran. */
function FilaValoracion({
  businessId,
  employeeId,
  valoracion,
  certificado,
  puedeAbrirSalud,
  puedeAnular,
}: {
  businessId: string
  employeeId: string
  valoracion: Valoracion
  certificado: {id: string; filename: string; de_salud: boolean} | undefined
  puedeAbrirSalud: boolean
  puedeAnular: boolean
}) {
  const {t} = useTranslation()
  return (
    <li className="flex flex-wrap items-start justify-between gap-3 py-3" data-testid={`valoracion-${valoracion.id}`}>
      <div className="min-w-0">
        <p className="text-sm font-medium text-foreground">
          {t(`valoracion.clase.${valoracion.clase}`)}
          <span className="ml-1.5 font-mono text-xs text-muted-foreground">{formatDateOnly(valoracion.fecha)}</span>
        </p>
        <p className="mt-0.5 text-xs text-muted-foreground">
          {t('valoracion.segunNorma', {norma: valoracion.norma_nombre})}
        </p>
        {valoracion.vence_el && (
          <p className="mt-0.5 font-mono text-[11px] text-muted-foreground">
            {t(
              valoracion.estado === 'vencido' && !valoracion.no_habilita ? 'valoracion.vencioEl' : 'valoracion.venceEl',
              {
                fecha: formatDateOnly(valoracion.vence_el),
              }
            )}
            {valoracion.vigencia_base && ` · ${t(`valoracion.base.${valoracion.vigencia_base}`)}`}
          </p>
        )}
        {valoracion.no_habilita && (
          <p className="mt-0.5 text-xs text-muted-foreground" data-testid={`valoracion-no-habilita-${valoracion.id}`}>
            {t('valoracion.noHabilita')}
          </p>
        )}
        {valoracion.sin_periodicidad && (
          <p
            className="mt-0.5 text-xs text-muted-foreground"
            data-testid={`valoracion-sin-periodicidad-${valoracion.id}`}>
            {t('valoracion.sinPeriodicidad')}
          </p>
        )}
        {valoracion.fecha_futura && (
          <p className="mt-0.5 text-xs text-muted-foreground">{t('valoracion.fechaFutura')}</p>
        )}
        {valoracion.acortamiento_motivo && (
          <p className="mt-0.5 text-xs text-muted-foreground">
            {t('valoracion.acortada', {motivo: valoracion.acortamiento_motivo})}
          </p>
        )}
        {valoracion.aportada_no_exigida && (
          <p className="mt-0.5 text-xs text-muted-foreground" data-testid={`valoracion-aportada-${valoracion.id}`}>
            {t('valoracion.aportada')}
          </p>
        )}
        {valoracion.no_exigible && (
          <p
            className="mt-0.5 text-xs text-muted-foreground"
            data-testid={`valoracion-ya-no-exigible-${valoracion.id}`}>
            {valoracion.no_exigible_desde
              ? t('valoracion.yaNoExigible', {fecha: formatDateOnly(valoracion.no_exigible_desde)})
              : t('valoracion.yaNoExigibleSinFecha')}
          </p>
        )}
        {valoracion.anulado_en && (
          <p className="mt-0.5 text-xs text-muted-foreground" data-testid={`valoracion-anulada-${valoracion.id}`}>
            {t('valoracion.anulada', {
              fecha: formatDateOnly(valoracion.anulado_en),
              motivo: valoracion.anulado_motivo ?? '',
            })}
          </p>
        )}
        {certificado && (
          <DescargarEvidencia
            ruta={rutaDeDescarga(businessId, certificado.id)}
            reservado={certificado.de_salud && !puedeAbrirSalud}
            className="mt-0.5 block w-full truncate text-xs font-medium text-primary hover:underline"
            testId={`valoracion-certificado-${valoracion.id}`}>
            {t('valoracion.certificado')}: {certificado.filename}
          </DescargarEvidencia>
        )}
      </div>
      <div className="flex items-center gap-1">
        {valoracion.veredicto && (
          <Tag tone={soporteEstadoTone(valoracion.estado)} data-testid={`valoracion-veredicto-${valoracion.id}`}>
            {valoracion.veredicto}
          </Tag>
        )}
        {puedeAnular && !valoracion.anulado_en && (
          <AnularValoracionDialog businessId={businessId} employeeId={employeeId} valoracion={valoracion} />
        )}
      </div>
    </li>
  )
}

/** E1-H09: examen aparte del concepto (D-P), con certificado, veredicto y vigencia propios. */
export function ValoracionesSection({
  businessId,
  employeeId,
  canOperate,
}: {
  businessId: string
  employeeId: string | null
  canOperate: boolean
}) {
  const {t} = useTranslation()
  const godReadOnly = useGodModeReadOnly()
  const capabilities = useCapabilities(businessId)
  const [page, setPage] = useState(1)
  const {data, isLoading, isError, error} = useValoraciones(businessId, employeeId, page)
  const cifras = useCifras(businessId)
  const {data: evidenceYears} = useEmployeeEvidence(businessId, employeeId)
  const [fijandoCifra, setFijandoCifra] = useState(false)
  const [registrando, setRegistrando] = useState<{clase: ValoracionClase | null} | null>(null)

  if (!canOperate) return null
  if (isLoading) return <Skeleton className="h-20 w-full" />
  const puedeRegistrar = capabilities.sgsst_firmar && !godReadOnly && !!employeeId
  // El modal no monta sin la cifra cargada: sin ella el clic no haría nada.
  const puedeFijar = capabilities.sgsst_firmar && !godReadOnly && !!cifras.data
  const bloqueadoPor = godReadOnly ? t('valoracion.bloqueoModoDios') : capabilities.firmar_bloqueado_por
  const catalogo = data?.catalogo ?? []
  // La primera página de soportes (diez años, R6) basta: solo se buscan certificados ya nombrados por `evidence_id`.
  const certificados = new Map(
    (evidenceYears?.items ?? []).flatMap((year) => year.items.map((item) => [item.id, item] as const))
  )
  return (
    <div className="space-y-3 border-t border-border pt-4" data-testid="empleado-valoraciones">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="min-w-0">
          <h3 className="flex items-center gap-1.5 text-sm font-medium text-foreground">
            {t('valoracion.titulo')}
            <HelpIcon
              label={t('common.help.iconLabelFor', {campo: t('valoracion.titulo')})}
              testId="valoracion-criterio-help">
              {t('valoracion.descripcion')}
            </HelpIcon>
          </h3>
        </div>
        {puedeRegistrar && employeeId ? (
          <Button size="sm" data-testid="valoracion-registrar-abrir" onClick={() => setRegistrando({clase: null})}>
            {t('valoracion.registrar.action')}
          </Button>
        ) : (
          bloqueadoPor && (
            <p className="text-xs text-muted-foreground" data-testid="valoracion-registro-bloqueado">
              {bloqueadoPor}
            </p>
          )
        )}
      </div>
      {isError ? (
        <p className="text-sm text-destructive" data-testid="empleado-valoraciones-error">
          {translateApiError(error)}
        </p>
      ) : !data || (data.exigencias.length === 0 && data.items.length === 0) ? (
        <div className="space-y-1 text-sm text-muted-foreground" data-testid="empleado-valoraciones-empty">
          <p>{t('valoracion.empty')}</p>
          <p className="flex flex-wrap items-center gap-1.5 text-xs">
            <Link to={`/businesses/${businessId}/sgsst/perfiles-cargo`} className="text-primary hover:underline">
              {t('valoracion.verPerfilCargo')}
            </Link>
            <PorQue label={t('common.porQue')} testId="empleado-valoraciones-empty-porque">
              <p>{t('valoracion.emptyPerfil')}</p>
            </PorQue>
          </p>
        </div>
      ) : (
        <div className="space-y-3">
          {data.exigencias.length > 0 && (
            <div>
              <p className="font-mono text-[10px] font-medium tracking-wider text-muted-foreground uppercase">
                {t('valoracion.exigidas')}
              </p>
              <ul className="divide-y divide-border" data-testid="valoracion-exigencias">
                {data.exigencias.map((exigencia) => (
                  <FilaExigencia
                    key={exigencia.clase}
                    exigencia={exigencia}
                    norma={catalogo.find((fila) => fila.clase === exigencia.clase)}
                    puedeFijar={puedeFijar}
                    puedeRegistrar={puedeRegistrar}
                    onFijarCifra={() => setFijandoCifra(true)}
                    onRegistrar={() => setRegistrando({clase: exigencia.clase})}
                  />
                ))}
              </ul>
            </div>
          )}
          {data.items.length > 0 && (
            <div>
              <p className="font-mono text-[10px] font-medium tracking-wider text-muted-foreground uppercase">
                {t('valoracion.registradas')}
              </p>
              <ul className="divide-y divide-border" data-testid="valoracion-lista">
                {data.items.map((valoracion) => (
                  <FilaValoracion
                    key={valoracion.id}
                    businessId={businessId}
                    employeeId={employeeId ?? ''}
                    valoracion={valoracion}
                    certificado={valoracion.evidence_id ? certificados.get(valoracion.evidence_id) : undefined}
                    puedeAbrirSalud={capabilities.sgsst_custodiar_salud}
                    puedeAnular={puedeRegistrar}
                  />
                ))}
              </ul>
              {/* R6: el pie va aunque haya una sola página; el total es lo que una lista recortada no dice. */}
              {data.total > 0 && (
                <PaginationBar page={data.page} pageSize={data.page_size} total={data.total} onPageChange={setPage} />
              )}
            </div>
          )}
          {/* U6: un «¿Por qué?» suelto bajo la lista no dice a qué se refiere. */}
          <PorQue label={t('valoracion.semillaLabel')} testId="valoracion-semilla">
            <p>{t('valoracion.semilla')}</p>
          </PorQue>
        </div>
      )}
      {registrando && employeeId && (
        <RegistrarValoracionDialog
          businessId={businessId}
          employeeId={employeeId}
          catalogo={catalogo}
          claseInicial={registrando.clase}
          open
          onOpenChange={(next) => {
            if (next) return
            setRegistrando(null)
            setPage(1)
          }}
        />
      )}
      <CifraModal
        open={fijandoCifra}
        onOpenChange={() => setFijandoCifra(false)}
        businessId={businessId}
        cifra={cifras.data?.cifras.find((fila) => fila.cifra_key === CIFRA_VIGENCIA) ?? null}
      />
    </div>
  )
}
